// src/pages/PostDetail/index.jsx - 修复版本
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useArticles } from '../../contexts/ArticleContext';
import AuthGuard from '../../components/AuthGuard';
import { 
  Button, 
  Modal, 
  message, 
  Space, 
  Tag,
  Popconfirm,
  Divider,
  Skeleton,
  Breadcrumb,
  Affix,
  FloatButton,
  Form,
  Input,
  Select
} from 'antd';
import { 
  DeleteOutlined, 
  EditOutlined, 
  LeftOutlined, 
  ShareAltOutlined,
  HomeOutlined,
  UserOutlined,
  CalendarOutlined,
  TagOutlined,
  ArrowUpOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  ReadOutlined,
  BookOutlined,
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import './index.css';

const { Option } = Select;

// 简单的编辑器组件
const SimpleEditor = ({ initialData, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [tags, setTags] = useState(initialData.tags || []);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    form.setFieldsValue({
      title: initialData.title || '',
      content: initialData.content || '',
      category: initialData.category || '技术',
      excerpt: initialData.excerpt || '',
      author: initialData.author || ''
    });
    setTags(initialData.tags || []);
  }, [initialData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!values.content || !values.content.trim()) {
        message.error('请填写文章内容');
        return;
      }

      const articleData = {
        ...values,
        tags,
        updatedAt: new Date().toISOString()
      };

      await onSave(articleData);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const handleTagInputConfirm = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const removeTag = (removedTag) => {
    const newTags = tags.filter(tag => tag !== removedTag);
    setTags(newTags);
  };

  const commonTags = [
    '技术', '前端', 'React', 'JavaScript', 'CSS', 
    'Node.js', 'TypeScript', 'Vue', 'Web开发', '编程',
    '生活', '思考', '读书', '旅行'
  ];

  return (
    <div className="simple-editor">
      <Form
        form={form}
        layout="vertical"
      >
        <div className="form-row">
          <Form.Item
            name="title"
            label="文章标题"
            rules={[
              { required: true, message: '请输入文章标题' },
              { max: 100, message: '标题不能超过100个字符' }
            ]}
            style={{ flex: 2 }}
          >
            <Input 
              placeholder="请输入文章标题" 
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="category"
            label="分类"
            rules={[{ required: true, message: '请选择文章分类' }]}
            style={{ flex: 1 }}
          >
            <Select placeholder="选择分类">
              <Option value="技术">技术</Option>
              <Option value="前端">前端</Option>
              <Option value="后端">后端</Option>
              <Option value="移动开发">移动开发</Option>
              <Option value="人工智能">人工智能</Option>
              <Option value="生活">生活</Option>
              <Option value="读书">读书</Option>
              <Option value="其他">其他</Option>
            </Select>
          </Form.Item>
        </div>

        <Form.Item
          name="excerpt"
          label="文章摘要"
        >
          <Input.TextArea
            placeholder="请输入文章摘要（可选）"
            rows={3}
            maxLength={300}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="author"
          label="作者"
        >
          <Input 
            placeholder="作者名称" 
          />
        </Form.Item>

        <div className="editor-field">
          <label>文章标签</label>
          <div className="tags-container">
            <div className="current-tags">
              {tags.map(tag => (
                <Tag 
                  key={tag} 
                  closable 
                  onClose={() => removeTag(tag)}
                  className="article-tag"
                >
                  {tag}
                </Tag>
              ))}
            </div>
            
            <div className="tag-input-area">
              <Input
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onBlur={handleTagInputConfirm}
                onPressEnter={handleTagInputConfirm}
                placeholder="输入标签后按回车"
                className="tag-input"
              />
              <span className="tag-input-tip">按回车添加标签</span>
            </div>

            <div className="common-tags">
              <span className="common-tags-label">常用标签：</span>
              {commonTags.map(tag => (
                <Tag 
                  key={tag}
                  className="common-tag"
                  onClick={() => {
                    if (!tags.includes(tag)) {
                      setTags([...tags, tag]);
                    }
                  }}
                >
                  {tag}
                </Tag>
              ))}
            </div>
          </div>
        </div>

        <Form.Item
          name="content"
          label="文章内容"
          rules={[
            { required: true, message: '请输入文章内容' },
            { min: 50, message: '内容太短，请至少输入50个字' }
          ]}
        >
          <Input.TextArea
            placeholder="开始编辑文章内容..."
            rows={20}
            style={{ fontFamily: 'Consolas, Monaco, monospace' }}
          />
        </Form.Item>
      </Form>

      <div className="editor-actions">
        <Space>
          <Button 
            onClick={onCancel}
            icon={<CloseOutlined />}
          >
            取消
          </Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            icon={<SaveOutlined />}
          >
            保存修改
          </Button>
        </Space>
      </div>
    </div>
  );
};

function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getArticle, deleteArticle, updateArticle } = useArticles();
  const contentRef = useRef(null);
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [editorVisible, setEditorVisible] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [readingTime, setReadingTime] = useState(5);
  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    loadPost();
  }, [id]);

  useEffect(() => {
    if (post?.content) {
      const words = post.content.trim().split(/\s+/).length;
      setWordCount(words);
      setReadingTime(Math.ceil(words / 200));
    }
  }, [post]);

  // 加载文章
  const loadPost = async () => {
    try {
      setLoading(true);
      
      const foundPost = getArticle(id);
      
      if (foundPost) {
        setPost(foundPost);
        
        // 更新阅读计数
        updateArticleViewCount(foundPost.id);
        
        // 加载相关文章
        loadRelatedPosts(foundPost);
      } else {
        message.error('文章未找到');
        navigate('/archive');
      }
    } catch (error) {
      console.error('加载文章失败:', error);
      message.error('加载文章失败');
    } finally {
      setLoading(false);
    }
  };

  // 更新阅读计数
  const updateArticleViewCount = (articleId) => {
    try {
      const savedPosts = JSON.parse(localStorage.getItem('blog_articles')) || [];
      const updatedPosts = savedPosts.map(article => {
        if (article.id === articleId) {
          return {
            ...article,
            views: (article.views || 0) + 1,
            lastRead: new Date().toISOString()
          };
        }
        return article;
      });
      
      localStorage.setItem('blog_articles', JSON.stringify(updatedPosts));
    } catch (error) {
      console.error('更新阅读计数失败:', error);
    }
  };

  // 加载相关文章
  const loadRelatedPosts = (currentPost) => {
    try {
      const savedPosts = JSON.parse(localStorage.getItem('blog_articles')) || [];
      
      const related = savedPosts
        .filter(p => 
          p.id !== currentPost.id && 
          (p.category === currentPost.category || 
           (p.tags && p.tags.some(tag => 
              currentPost.tags && currentPost.tags.includes(tag)
            )))
        )
        .slice(0, 3);
      
      setRelatedPosts(related);
    } catch (error) {
      console.error('加载相关文章失败:', error);
    }
  };

  // 阅读进度跟踪
  useEffect(() => {
    const handleScroll = () => {
      const contentElement = contentRef.current;
      if (!contentElement) return;
      
      const contentHeight = contentElement.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrolled = (scrollTop - contentElement.offsetTop + windowHeight) / contentHeight;
      setReadingProgress(Math.min(Math.max(scrolled, 0), 1) * 100);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);

  // 检查是否是文章作者
  const isAuthor = post && user && (
    post.authorId === user.id || 
    post.author === user.username || 
    user.role === 'admin'
  );

  // 删除文章
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteArticle(post.id);
      message.success('文章删除成功');
      navigate('/archive');
    } catch (error) {
      console.error('删除文章失败:', error);
      message.error('删除文章失败');
    } finally {
      setDeleting(false);
    }
  };

  // 保存编辑后的文章
  const handleSaveEdit = async (updatedData) => {
    try {
      await updateArticle(post.id, updatedData);
      setPost(prev => ({ ...prev, ...updatedData }));
      setEditorVisible(false);
      message.success('文章更新成功');
    } catch (error) {
      console.error('保存编辑失败:', error);
      message.error('保存失败');
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '未知日期';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = now - date;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return '今天';
      if (diffDays === 1) return '昨天';
      if (diffDays < 7) return `${diffDays}天前`;
      
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // 分享文章
  const handleShare = () => {
    const url = window.location.href;
    const title = post?.title || '';
    
    if (navigator.share) {
      navigator.share({
        title: title,
        text: post?.excerpt || '',
        url: url
      }).catch(err => {
        console.log('分享取消:', err);
      });
    } else {
      navigator.clipboard.writeText(url)
        .then(() => message.success('链接已复制到剪贴板'))
        .catch(() => message.error('复制失败'));
    }
  };

  // 滚动到顶部
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 计算目录
  const generateTOC = () => {
    if (!post?.content) return [];
    
    const headings = post.content.match(/#{1,3}\s+(.+)/g) || [];
    return headings.map((heading, index) => {
      const title = heading.replace(/^#{1,3}\s+/, '');
      const level = heading.match(/^#+/)[0].length;
      const id = `heading-${index}`;
      
      return { title, level, id };
    });
  };

  const toc = generateTOC();

  if (loading) {
    return (
      <div className="post-detail">
        <div className="container">
          <div className="post-skeleton">
            <Skeleton active paragraph={{ rows: 15 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="post-detail">
        <div className="container">
          <div className="post-not-found">
            <div className="not-found-icon">📄</div>
            <h2>文章未找到</h2>
            <p>您要查找的文章可能已被删除或不存在</p>
            <Space>
              <Button type="primary" onClick={() => navigate('/')} icon={<HomeOutlined />}>
                返回首页
              </Button>
              <Button onClick={() => navigate('/archive')} icon={<BookOutlined />}>
                浏览归档
              </Button>
            </Space>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="post-detail">
      {/* 阅读进度条 */}
      <div className="reading-progress">
        <div className="progress-bar" style={{ width: `${readingProgress}%` }}></div>
      </div>
      
      {/* 面包屑导航 */}
      <div className="breadcrumb-wrapper">
        <div className="container">
          <Breadcrumb
            items={[
              { 
                title: <span><HomeOutlined /> 首页</span>, 
                onClick: () => navigate('/') 
              },
              { 
                title: '文章归档', 
                onClick: () => navigate('/archive') 
              },
              { 
                title: post.category || '未分类', 
                onClick: () => navigate(`/archive?category=${post.category}`) 
              },
              { title: post.title }
            ]}
          />
        </div>
      </div>

      <div className="container">
        <div className="post-layout">
          {/* 侧边栏 - 文章信息 */}
          <aside className="post-sidebar">
            <Affix offsetTop={80}>
              <div className="sidebar-content">
                <div className="author-card">
                  <div className="author-avatar">
                    {post.authorAvatar ? (
                      <img src={post.authorAvatar} alt={post.author} />
                    ) : (
                      <div className="avatar-placeholder">
                        <UserOutlined />
                      </div>
                    )}
                  </div>
                  <h4 className="author-name">{post.author || '匿名作者'}</h4>
                  {post.authorBio && (
                    <p className="author-bio">{post.authorBio}</p>
                  )}
                </div>

                <div className="post-stats">
                  <div className="stat-item">
                    <ReadOutlined />
                    <div className="stat-info">
                      <div className="stat-value">{readingTime}分钟</div>
                      <div className="stat-label">阅读时间</div>
                    </div>
                  </div>
                  <div className="stat-item">
                    <BookOutlined />
                    <div className="stat-info">
                      <div className="stat-value">{wordCount}</div>
                      <div className="stat-label">总字数</div>
                    </div>
                  </div>
                  <div className="stat-item">
                    <EyeOutlined />
                    <div className="stat-info">
                      <div className="stat-value">{post.views || 0}</div>
                      <div className="stat-label">阅读次数</div>
                    </div>
                  </div>
                </div>

                {/* 目录 */}
                {toc.length > 0 && (
                  <div className="toc-section">
                    <h4 className="toc-title">📑 文章目录</h4>
                    <ul className="toc-list">
                      {toc.map((item, index) => (
                        <li 
                          key={index} 
                          className={`toc-item level-${item.level}`}
                          onClick={() => {
                            const element = document.getElementById(item.id);
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth' });
                            }
                          }}
                        >
                          {item.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* 操作按钮 */}
                <div className="sidebar-actions">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Button 
                      block 
                      icon={<ShareAltOutlined />}
                      onClick={handleShare}
                      size="large"
                    >
                      分享文章
                    </Button>
                    <Button 
                      block 
                      icon={<LeftOutlined />}
                      onClick={() => navigate('/archive')}
                      size="large"
                    >
                      返回列表
                    </Button>
                    {isAuthor && (
                      <>
                        <Button 
                          block 
                          type="primary" 
                          icon={<EditOutlined />}
                          onClick={() => setEditorVisible(true)}
                          size="large"
                        >
                          编辑文章
                        </Button>
                        <Popconfirm
                          title="确定要删除这篇文章吗？"
                          description="删除后无法恢复"
                          onConfirm={handleDelete}
                          okText="确定"
                          cancelText="取消"
                          okType="danger"
                        >
                          <Button 
                            block 
                            danger 
                            icon={<DeleteOutlined />}
                            loading={deleting}
                            size="large"
                          >
                            删除文章
                          </Button>
                        </Popconfirm>
                      </>
                    )}
                  </Space>
                </div>
              </div>
            </Affix>
          </aside>

          {/* 主内容区 */}
          <main className="post-main">
            {/* 作者操作栏 */}
            {isAuthor && (
              <div className="author-actions">
                <Space>
                  <Tag color="blue" icon={<UserOutlined />}>作者模式</Tag>
                  <Button 
                    type="primary" 
                    icon={<EditOutlined />}
                    onClick={() => setEditorVisible(true)}
                    size="small"
                  >
                    编辑文章
                  </Button>
                  <Popconfirm
                    title="确定要删除这篇文章吗？"
                    description="删除后无法恢复"
                    onConfirm={handleDelete}
                    okText="确定"
                    cancelText="取消"
                    okType="danger"
                  >
                    <Button 
                      danger 
                      icon={<DeleteOutlined />}
                      loading={deleting}
                      size="small"
                    >
                      删除文章
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            )}

            {/* 文章内容 */}
            <article className="post-content" ref={contentRef}>
              <header className="post-header">
                <div className="post-category-tag">
                  {post.category && (
                    <Tag color="blue" className="category-tag">
                      {post.category}
                    </Tag>
                  )}
                </div>
                
                <h1 className="post-title">
                  {post.title}
                </h1>
                
                <div className="post-meta">
                  <Space size="large" wrap>
                    <div className="meta-item">
                      <UserOutlined />
                      <span className="meta-text">
                        {post.author || '匿名作者'}
                      </span>
                    </div>
                    <div className="meta-item">
                      <CalendarOutlined />
                      <span className="meta-text">
                        {formatDate(post.date || post.createdAt)}
                      </span>
                    </div>
                    <div className="meta-item">
                      <ClockCircleOutlined />
                      <span className="meta-text">
                        {readingTime}分钟阅读
                      </span>
                    </div>
                    {post.updatedAt && post.updatedAt !== post.createdAt && (
                      <div className="meta-item">
                        <EditOutlined />
                        <span className="meta-text">
                          更新于: {formatDate(post.updatedAt)}
                        </span>
                      </div>
                    )}
                  </Space>
                </div>
                
                {/* 标签 */}
                {post.tags && post.tags.length > 0 && (
                  <div className="post-tags">
                    <TagOutlined className="tags-icon" />
                    <Space wrap>
                      {post.tags.map((tag, index) => (
                        <Tag 
                          key={index} 
                          color="default"
                          className="post-tag-item"
                          onClick={() => navigate(`/archive?tag=${tag}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          #{tag}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}
              </header>

              <Divider />

              {/* 文章正文 */}
              <div className="post-body">
                <div className="content-wrapper">
                  <div className="content-text">
                    {post.content.split('\n').map((paragraph, index) => {
                      // 处理标题
                      if (paragraph.match(/^#{1,3}\s+/)) {
                        const level = paragraph.match(/^#+/)[0].length;
                        const title = paragraph.replace(/^#{1,3}\s+/, '');
                        const id = `heading-${index}`;
                        
                        const HeadingTag = `h${level + 1}`;
                        return React.createElement(
                          HeadingTag,
                          { 
                            key: index, 
                            id: id,
                            className: 'content-heading'
                          },
                          title
                        );
                      }
                      
                      // 处理普通段落
                      return paragraph.trim() ? (
                        <p key={index} className="paragraph">
                          {paragraph}
                        </p>
                      ) : (
                        <br key={index} />
                      );
                    })}
                  </div>
                </div>
              </div>

              <Divider />

              {/* 文章页脚 */}
              <footer className="post-footer">
                <div className="footer-actions">
                  <Space wrap>
                    <Button 
                      icon={<LeftOutlined />}
                      onClick={() => navigate(-1)}
                      size="large"
                    >
                      返回
                    </Button>
                    <Button 
                      type="primary" 
                      icon={<HomeOutlined />}
                      onClick={() => navigate('/')}
                      size="large"
                    >
                      回到首页
                    </Button>
                    <Button 
                      icon={<ShareAltOutlined />}
                      onClick={handleShare}
                      size="large"
                    >
                      分享文章
                    </Button>
                  </Space>
                </div>
                
                {/* 版权声明 */}
                <div className="copyright-notice">
                  <p>© {new Date().getFullYear()} {post.author || '作者'}。保留所有权利。</p>
                  <p className="copyright-note">
                    本文为原创内容，转载请注明出处。
                  </p>
                </div>
              </footer>
            </article>

            {/* 相关文章 */}
            {relatedPosts.length > 0 && (
              <div className="related-posts">
                <h3 className="related-title">
                  <span className="related-icon">📚</span>
                  相关文章推荐
                </h3>
                <div className="related-grid">
                  {relatedPosts.map(relatedPost => (
                    <div 
                      key={relatedPost.id} 
                      className="related-card"
                      onClick={() => navigate(`/article/${relatedPost.id}`)}
                    >
                      <h4 className="related-card-title">
                        {relatedPost.title}
                      </h4>
                      <p className="related-card-excerpt">
                        {relatedPost.excerpt || relatedPost.content?.substring(0, 100)}...
                      </p>
                      <div className="related-card-meta">
                        <span className="related-card-author">
                          {relatedPost.author}
                        </span>
                        <span className="related-card-date">
                          {formatDate(relatedPost.date)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* 浮动按钮 */}
      <FloatButton.Group shape="circle" style={{ right: 24 }}>
        <FloatButton 
          icon={<ArrowUpOutlined />} 
          onClick={scrollToTop}
          tooltip="回到顶部"
        />
        <FloatButton 
          icon={<ShareAltOutlined />} 
          onClick={handleShare}
          tooltip="分享文章"
        />
        {isAuthor && (
          <FloatButton 
            icon={<EditOutlined />} 
            onClick={() => setEditorVisible(true)}
            tooltip="编辑文章"
          />
        )}
      </FloatButton.Group>

      {/* 编辑文章模态框 */}
      <AuthGuard>
        <Modal
          title="编辑文章"
          open={editorVisible}
          onCancel={() => setEditorVisible(false)}
          footer={null}
          width={900}
          destroyOnClose
          maskClosable={false}
          style={{ top: 20 }}
        >
          <SimpleEditor
            initialData={post}
            onSave={handleSaveEdit}
            onCancel={() => setEditorVisible(false)}
          />
        </Modal>
      </AuthGuard>
    </div>
  );
}

export default PostDetail;